import { ref, watch, type Ref } from 'vue';
import { trainingMatrixApi, type TrainingMatrixDocumentEntry } from '@/entities/Training';
import { documentApi } from '@/entities/Document';
import { useDocuments } from '@/features/Document';
import { useCustomToast } from '@/shared/lib';

export function useOnboardingDocuments(positionId: Ref<number | null>, revisionId: Ref<number | null>) {
  const toast = useCustomToast();
  const { viewDocument } = useDocuments();

  const documents = ref<TrainingMatrixDocumentEntry[]>([]);
  const loading = ref(false);
  let latestRequestId = 0;

  async function fetchDocuments(): Promise<void> {
    const requestId = ++latestRequestId;

    if (positionId.value === null || revisionId.value === null) {
      documents.value = [];
      loading.value = false;
      return;
    }

    loading.value = true;
    try {
      const result = await trainingMatrixApi.getRevisionDetail(revisionId.value, {
        positionId: positionId.value,
        allItems: true,
      });
      if (requestId !== latestRequestId) return;
      documents.value = result.data.positions.find((position) => position.id === positionId.value)?.documents ?? [];
    } catch {
      if (requestId !== latestRequestId) return;
      documents.value = [];
      toast.showErrorToast({
        title: 'Erro ao carregar documentos',
        message: 'Não foi possível carregar os documentos da matriz de treinamentos.',
        type: 'error',
      });
    } finally {
      if (requestId === latestRequestId) loading.value = false;
    }
  }

  async function viewDocumentRevision(documentRevisionId: number): Promise<void> {
    try {
      const revision = await documentApi.getRevisionById(documentRevisionId);
      await viewDocument(revision.document.id.toString(), undefined, documentRevisionId);
    } catch {
      toast.showErrorToast({
        title: 'Erro ao abrir documento',
        message: 'Não foi possível abrir este documento.',
        type: 'error',
      });
    }
  }

  watch([positionId, revisionId], fetchDocuments, { immediate: true });

  return { documents, loading, viewDocumentRevision };
}
